import React, { useEffect, useState } from 'react';
import apiService from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import UserManagement from './UserManagement';
import GoogleCalendarSettings from './GoogleCalendarSettings';

type Tab = 'email' | 'users' | 'gcal';

interface EmailSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  passSet: boolean;
}

const DEFAULTS: EmailSettings = {
  host: 'w014e07b.kasserver.com',
  port: 587,
  secure: false,
  user: 'info@seadent.es',
  passSet: false,
};

const SettingsPage: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState<Tab>('email');
  const [settings, setSettings] = useState<EmailSettings>(DEFAULTS);
  const [pass, setPass] = useState(''); // empty means "leave unchanged"
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    let mounted = true;
    apiService.get<EmailSettings>('/settings/email')
      .then(resp => {
        if (mounted && resp.data?.success) {
          setSettings(resp.data.data);
        }
      })
      .catch(() => {
        if (mounted) addToast('Konnte Email-Einstellungen nicht laden — Defaults vorausgefüllt.', 'error');
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [addToast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: any = {
        host: settings.host,
        port: settings.port,
        secure: settings.secure,
        user: settings.user,
      };
      if (pass) body.pass = pass;
      const resp = await apiService.put<EmailSettings>('/settings/email', body);
      if (resp.data?.success) {
        setSettings(resp.data.data);
        setPass('');
        addToast('Email-Einstellungen gespeichert.', 'success');
      } else {
        addToast(resp.data?.message || 'Speichern fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testTo.trim()) {
      addToast('Bitte eine Empfänger-Adresse für den Test angeben.', 'error');
      return;
    }
    setTesting(true);
    try {
      const resp = await apiService.post<any>('/settings/email/test', { to: testTo.trim() });
      if (resp.data?.success) {
        addToast(`Test-Email akzeptiert (${resp.data.data?.messageId || 'OK'}).`, 'success');
      } else {
        addToast(resp.data?.message || 'Test fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      const err = e?.response?.data;
      const detail = err?.error?.response || err?.message || 'Test fehlgeschlagen.';
      addToast(`Test fehlgeschlagen: ${detail}`, 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading && tab === 'email') {
    return <div className="p-6 text-gray-500">Lade Einstellungen…</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-600">System-Einstellungen für SeaDent Dental Practice Management.</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Settings-Tabs">
          <button
            onClick={() => setTab('email')}
            className={`py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
              tab === 'email' ? 'border-navy-admin text-navy-admin' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ✉️ Email
          </button>
          {isAdmin && (
            <button
              onClick={() => setTab('users')}
              className={`py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
                tab === 'users' ? 'border-navy-admin text-navy-admin' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              👥 Benutzer
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setTab('gcal')}
              className={`py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
                tab === 'gcal' ? 'border-navy-admin text-navy-admin' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📅 Google Calendar
            </button>
          )}
        </nav>
      </div>

      {tab === 'users' && isAdmin && <UserManagement />}
      {tab === 'gcal' && isAdmin && <GoogleCalendarSettings />}

      {tab === 'email' && (
      <>
      <section className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">✉️ Email-Versand (SMTP)</h2>
          <p className="text-sm text-gray-600 mt-1">
            Konfiguration für ausgehende Mails (Terminbestätigungen, Stornierungen). All-Inkl-Mail-Konto unter <code>info@seadent.es</code>.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Host</label>
            <input
              type="text"
              value={settings.host}
              onChange={(e) => setSettings({ ...settings, host: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Port</label>
            <input
              type="number"
              value={settings.port}
              onChange={(e) => setSettings({ ...settings, port: parseInt(e.target.value || '0', 10) })}
              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none"
            />
            <p className="text-[11px] text-gray-500 mt-1">587 = STARTTLS (Hetzner-tauglich), 465 = SSL (von Hetzner geblockt).</p>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Benutzer</label>
            <input
              type="text"
              value={settings.user}
              onChange={(e) => setSettings({ ...settings, user: e.target.value })}
              autoComplete="username"
              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
              Passwort {settings.passSet && <span className="text-green-600 font-normal normal-case">(gespeichert)</span>}
            </label>
            <input
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder={settings.passSet ? '•••••• (leer lassen → unverändert)' : 'Passwort eingeben'}
              autoComplete="new-password"
              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none"
            />
          </div>

          <div className="md:col-span-2 flex items-center space-x-2">
            <input
              id="secure"
              type="checkbox"
              checked={settings.secure}
              onChange={(e) => setSettings({ ...settings, secure: e.target.checked })}
              className="h-4 w-4"
            />
            <label htmlFor="secure" className="text-sm text-gray-700">
              SSL direkt nutzen (<code>secure=true</code>) — nur sinnvoll bei Port 465. Bei 587 bitte ausgelassen lassen (STARTTLS).
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-navy-admin text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
          >
            {saving ? 'Speichere…' : 'Speichern'}
          </button>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">🧪 Test-Email senden</h2>
          <p className="text-sm text-gray-600 mt-1">
            Prüft die SMTP-Verbindung mit den aktuell <strong>gespeicherten</strong> Werten und sendet eine kleine Test-Mail.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="empfaenger@example.com"
            className="flex-1 p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none"
          />
          <button
            onClick={handleTest}
            disabled={testing || !testTo.trim()}
            className="px-5 py-2 bg-light-blue text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
          >
            {testing ? 'Sende…' : 'Test senden'}
          </button>
        </div>
      </section>
      </>
      )}
    </div>
  );
};

export default SettingsPage;
